import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import { SettingsLayout } from './components/Layout';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { POS } from './pages/POS';
import { AdminStore } from './pages/AdminStore';
import { AdminProducts } from './pages/AdminProducts';
import { AdminUsers } from './pages/AdminUsers';
import { AdminStripe } from './pages/AdminStripe';
import { Transactions } from './pages/Transactions';
import { Reports } from './pages/Reports';
import { AdminPortalLogin } from './pages/admin/AdminPortalLogin';
import { AdminPortalLayout, AdminPortalDashboard } from './pages/admin/AdminPortalDashboard';
import { AdminPortalInvites } from './pages/admin/AdminPortalInvites';
import { AdminPortalAdmins } from './pages/admin/AdminPortalAdmins';
import { AdminPortalAuditLog } from './pages/admin/AdminPortalAuditLog';
import { CheckoutResult } from './pages/CheckoutResult';

function isRootDomain(): boolean {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) return true;
  const parts = hostname.split('.');
  return parts.length === 2 || (parts.length === 3 && parts[0] === 'www');
}

function PrivateRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { isAuthenticated, isLoading, role, organization } = useAuth();

  if (isLoading) return <div style={loadingStyle}>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (isRootDomain() && organization?.subdomain) {
    const protocol = window.location.protocol;
    const path = window.location.pathname;
    window.location.href = `${protocol}//${organization.subdomain}.taplo.cc${path}`;
    return <div style={loadingStyle}>Redirecting to {organization.subdomain}.taplo.cc...</div>;
  }

  if (allowedRoles && role && !allowedRoles.includes(role)) return <Navigate to="/pos" replace />;

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isRootDomain()) return <>{children}</>;
  if (isLoading) return <div style={loadingStyle}>Loading...</div>;
  if (isAuthenticated) return <Navigate to="/pos" replace />;

  return <>{children}</>;
}

function AdminPrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAdminAuth();
  if (isLoading) return <div style={loadingStyle}>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
}

function AdminPublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAdminAuth();
  if (isLoading) return <div style={loadingStyle}>Loading...</div>;
  if (isAuthenticated) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

function RootApp() {
  // On the root domain, the /admin/* namespace is the platform admin portal.
  // /register?token=... and /login still need to work for tenant flows.
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="/admin/login" element={<AdminPublicRoute><AdminPortalLogin /></AdminPublicRoute>} />
        <Route
          element={
            <AdminPrivateRoute>
              <AdminPortalLayout />
            </AdminPrivateRoute>
          }
        >
          <Route path="/admin" element={<AdminPortalDashboard />} />
          <Route path="/admin/invites" element={<AdminPortalInvites />} />
          <Route path="/admin/admins" element={<AdminPortalAdmins />} />
          <Route path="/admin/audit-logs" element={<AdminPortalAuditLog />} />
        </Route>

        {/* Tenant-facing root routes (sign in chooser + invite-redeem register) */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

        {/* Customer-facing Stripe Checkout return pages — no auth */}
        <Route path="/checkout/success" element={<CheckoutResult variant="success" />} />
        <Route path="/checkout/cancel" element={<CheckoutResult variant="cancel" />} />

        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}

function TenantApp() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

      {/* Customer-facing Stripe Checkout return pages — no auth */}
      <Route path="/checkout/success" element={<CheckoutResult variant="success" />} />
      <Route path="/checkout/cancel" element={<CheckoutResult variant="cancel" />} />

      {/* POS — full screen, no layout wrapper */}
      <Route path="/pos" element={<PrivateRoute><POS /></PrivateRoute>} />

      {/* Settings — sidebar layout, managers/owners only */}
      <Route
        element={
          <PrivateRoute allowedRoles={['owner', 'manager']}>
            <SettingsLayout />
          </PrivateRoute>
        }
      >
        <Route path="/settings" element={<Navigate to="/settings/store" replace />} />
        <Route path="/settings/store" element={<AdminStore />} />
        <Route path="/settings/products" element={<AdminProducts />} />
        <Route path="/settings/transactions" element={<Transactions />} />
        <Route path="/settings/reports" element={<Reports />} />
        <Route
          path="/settings/users"
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <AdminUsers />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/stripe"
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <AdminStripe />
            </PrivateRoute>
          }
        />
      </Route>

      <Route path="/" element={<Navigate to="/pos" replace />} />
      <Route path="*" element={<Navigate to="/pos" replace />} />
    </Routes>
  );
}

function DocumentTitle() {
  const { organization } = useAuth();
  useEffect(() => {
    const workspace = (organization?.displayName || organization?.name || '').trim();
    document.title = workspace ? `TaploPOS - ${workspace}` : 'TaploPOS';
  }, [organization?.displayName, organization?.name]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DocumentTitle />
        {isRootDomain() ? <RootApp /> : <TenantApp />}
      </AuthProvider>
    </BrowserRouter>
  );
}

const loadingStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: '100vh', fontSize: 18, color: 'var(--taplo-text-muted)',
};

export default App;
